import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    FaArchive, FaBookOpen, FaChartBar, FaDownload, FaEdit, FaExternalLinkAlt, FaEye, FaFileAlt,
    FaPlus, FaRegStar, FaSlidersH, FaStar,
} from "react-icons/fa";
import { GUIDES_CONSTANTS } from "../_MODULES/_API";
import { GuideGroupFormModal } from "./GuideGroupFormModal";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { FieldInput, FieldSelect } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { EmptyState } from "../../../../../_CORE/APP/Templates/_UI/Kit/_index";
import { useApiQuery } from "../../../../../_CORE/APP/Hooks/Hooks/_Records/_useApiQuery";
import { useApiActionMutation } from "../../../../../_CORE/APP/Hooks/Hooks/_Actions/_useApiActionMutation";
import { confirm } from "../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost";
import { onApiAction } from "../../../../_UI/onApiAction";
import { onSilentPatch } from "../../../../_UI/onSilentPatch";
import { CardActionMenu } from "../../../../_UI/CardActionMenu";
import { downloadMarkdownTreeZip, downloadPdfTree } from "../../../../_UI/DocumentExport";
import { SearchFilter, onMatchesSearch } from "../../../../_UI/SearchFilter";
import { ViewToolbar } from "../../../../_UI/ViewToolbar";
import { AdvancedFilterPanel } from "../../../../_UI/AdvancedFilterPanel";
import { StatisticsModal } from "../../../../_UI/StatisticsModal";
import { ViewPagination } from "../../../../_UI/ViewPagination";
import { usePagedList } from "../../../../_UI/usePagedList";
import "../../_Manuals/_UI/_Styles/Manuals.css";

const CATEGORY_LABEL = {
    GENERAL: "General", PROCESS: "Proceso", LANGUAGE: "Lenguaje", FRAMEWORK: "Framework",
    BACKEND: "Backend", FRONTEND: "Frontend", DATABASE: "Base de datos", DEVOPS: "DevOps",
    CLOUD: "Cloud", TOOL: "Herramienta", OTHER: "Otro",
};

const CATEGORY_OPTIONS = Object.entries(CATEGORY_LABEL).map(([value, label]) => ({ value, label }));

const onDate = (value) => value ? new Date(value).toLocaleDateString("es-AR", {
    day: "2-digit", month: "short", year: "numeric",
}) : "-";

const onSortGuidePages = (pages = []) => [...pages].sort((a, b) =>
    Number(a.position || 0) - Number(b.position || 0)
    || String(a.title || "").localeCompare(String(b.title || ""), "es"),
);

const onBuildGuideTree = (pages = []) => {
    const map = new Map();
    onSortGuidePages(pages).forEach((page) => map.set(page.id, { ...page, children: [] }));
    const roots = [];

    map.forEach((page) => {
        const parent = page.parentId ? map.get(page.parentId) : null;
        if (parent && parent.id !== page.id) parent.children.push(page);
        else roots.push(page);
    });

    const deepSort = (items) => {
        items.sort((a, b) => Number(a.position || 0) - Number(b.position || 0));
        items.forEach((item) => deepSort(item.children));
        return items;
    };

    return deepSort(roots);
};

export const PanelGuides = ({ projectId, subProjectId = null, scopeName = "Proyecto" }) => {
    const navigate = useNavigate();
    const [useIsOpen, setIsOpen] = useState(false);
    const [useEditing, setEditing] = useState(null);
    const [useSearch, setSearch] = useState("");
    const [useAdvancedOpen, setAdvancedOpen] = useState(false);
    const [statisticsOpen, setStatisticsOpen] = useState(false);
    const [useCategory, setCategory] = useState("ALL");
    const [useFavorite, setFavorite] = useState("ALL");
    const [useTechnology, setTechnology] = useState("");
    const [useVersion, setVersion] = useState("");

    const {
        apiGet: onGetGroups,
        useApiData: GROUPS,
        useApiLoading: IS_LOADING,
        mutateLocal,
    } = useApiQuery({ minTimeQuery: 220, enablePagination: false, startLoading: true });

    const { apiGet: onGetGuideForExport } = useApiQuery({
        minTimeQuery: 120, enablePagination: false, startLoading: false,
    });
    const [useExportingId, setExportingId] = useState("");

    const { actionMutate, useActionApiIsLoading } = useApiActionMutation({
        isToastError: true, isToastSuccess: true, minTimeMutate: 180,
    });

    const scopeBase = subProjectId
        ? `/projects/${projectId}/subprojects/${subProjectId}/guides`
        : `/projects/${projectId}/guides`;

    const onLoad = () => onGetGroups({
        endpoint: GUIDES_CONSTANTS.BY_PROJECT,
        config: {
            pathParams: { projectId },
            params: subProjectId ? { subProjectId } : {},
        },
    });

    useEffect(() => { onLoad(); }, [projectId, subProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

    const onSave = async (values) => {
        const isEdit = !!useEditing?.id;
        const previous = isEdit ? { ...useEditing } : null;
        const payload = isEdit ? values : { ...values, projectId, subProjectId: subProjectId || undefined };

        if (isEdit) mutateLocal({ id: useEditing.id, patch: values });

        const response = await actionMutate({
            method: isEdit ? "PATCH" : "POST",
            endpoint: isEdit ? GUIDES_CONSTANTS.UPDATE : GUIDES_CONSTANTS.CREATE,
            id: isEdit ? useEditing.id : null,
            data: payload,
            successMessage: isEdit ? "Grupo de guías actualizado" : "Grupo de guías creado",
        });

        if (!response.success) {
            if (isEdit && previous) mutateLocal({ id: previous.id, patch: previous });
            return false;
        }

        const responseData = response?.data?.data || response?.data || null;
        const createdId = !isEdit ? (responseData?.id || response?.id) : null;

        if (isEdit && responseData?.id) mutateLocal({ id: useEditing.id, patch: responseData });
        if (!isEdit && responseData?.id) mutateLocal({ insert: responseData });

        setEditing(null);
        if (createdId) window.setTimeout(() => navigate(`${scopeBase}/${createdId}?mode=edit`), 0);
        return true;
    };

    const onToggleFavorite = async (group) => {
        const previous = !!group.isFavorite;
        const next = !previous;
        mutateLocal({ id: group.id, patch: { isFavorite: next } });

        const response = await onSilentPatch({
            endpoint: GUIDES_CONSTANTS.UPDATE,
            id: group.id,
            data: { isFavorite: next },
            errorMessage: "No se pudo guardar el destacado del grupo de guías.",
        });

        if (!response.success) mutateLocal({ id: group.id, patch: { isFavorite: previous } });
    };

    const onArchive = async (group) => {
        const ok = await confirm.danger(
            "Archivar grupo de guías",
            `Se archiva "${group.name}" con todas sus guías. Lo podés restaurar o eliminar definitivamente desde Archivados.`,
        );
        if (!ok) return;

        mutateLocal({ id: group.id, remove: true });
        const response = await onApiAction({
            method: "PATCH", endpoint: GUIDES_CONSTANTS.ARCHIVE_ONE,
            pathParams: { id: group.id }, successMessage: "Grupo de guías archivado",
        });
        if (!response.success) mutateLocal({ insert: group });
    };

    const onExportGroup = async (group, format) => {
        if (!group?.id || useExportingId) return;
        setExportingId(group.id);

        try {
            const response = await onGetGuideForExport({
                endpoint: GUIDES_CONSTANTS.DETAIL,
                isDetail: true,
                config: { pathParams: { id: group.id } },
            });
            if (!response?.success || !response?.data) return;

            const detail = response.data;
            mutateLocal({ id: group.id, patch: { pageCount: Number(detail.pageCount ?? detail.pages?.length ?? group.pageCount ?? 0) } });
            const tree = onBuildGuideTree(detail.pages || []);
            const options = {
                title: detail.name || group.name || "Grupo de guías",
                description: detail.description || group.description || "",
                tree,
            };

            if (format === "pdf") downloadPdfTree(options);
            if (format === "markdown") downloadMarkdownTreeZip(options);
        } finally {
            setExportingId("");
        }
    };

    const rows = useMemo(() => Array.isArray(GROUPS) ? GROUPS : [], [GROUPS]);
    const filtered = useMemo(() => rows.filter((group) => {
        if (useCategory !== "ALL" && group.category !== useCategory) return false;
        if (useFavorite !== "ALL" && (!!group.isFavorite !== (useFavorite === "YES"))) return false;
        if (useTechnology.trim() && !onMatchesSearch(useTechnology, [group.technology])) return false;
        if (useVersion.trim() && !onMatchesSearch(useVersion, [group.version])) return false;
        return onMatchesSearch(useSearch, [
            group.name, group.description, group.technology, group.version, CATEGORY_LABEL[group.category], group.officialDocsUrl,
        ]);
    }).sort((a, b) => (Number(!!b.isFavorite) - Number(!!a.isFavorite)) || String(a.name ?? "").localeCompare(String(b.name ?? ""))),
    [rows, useSearch, useCategory, useFavorite, useTechnology, useVersion]);

    const totalPages = useMemo(() => rows.reduce((acc, item) => acc + Number(item.pageCount || 0), 0), [rows]);
    const advancedCount = [useCategory !== "ALL", useFavorite !== "ALL", !!useTechnology.trim(), !!useVersion.trim()].filter(Boolean).length;
    const pager = usePagedList(filtered, 6, "guidesPage");

    const clearAdvanced = () => {
        setCategory("ALL");
        setFavorite("ALL");
        setTechnology("");
        setVersion("");
    };

    const exportRows = filtered.map((group) => ({
        name: group.name || "",
        category: CATEGORY_LABEL[group.category] || group.category || "General",
        technology: group.technology || "",
        version: group.version || "",
        guides: Number(group.pageCount || 0),
        description: group.description || "",
        updatedAt: onDate(group.updatedAt),
    }));

    return (
        <>
            <div className="LStudio__app_panel_bar">
                <ViewToolbar
                    filters={<>
                        <SearchFilter value={useSearch} onChange={setSearch} total={rows.length} matches={filtered.length}
                            placeholder="Ej.: Deploy, React, Docker o autenticación..." />
                        <AppButton variant={useAdvancedOpen ? "soft" : "outline"} tone="neutral" icon={<FaSlidersH />}
                            onClick={() => setAdvancedOpen((current) => !current)}>
                            {advancedCount ? `Avanzado (${advancedCount})` : "Avanzado"}
                        </AppButton>
                    </>}
                    expandedContent={<AdvancedFilterPanel isOpen={useAdvancedOpen} activeCount={advancedCount} onClear={clearAdvanced}>
                        <FieldSelect id="guides_adv_category" label="Categoría" value={useCategory} onChange={(event) => setCategory(event.target.value)}
                            options={[{ value: "ALL", label: "Todas las categorías" }, ...CATEGORY_OPTIONS]} />
                        <FieldSelect id="guides_adv_favorite" label="Destacado" value={useFavorite} onChange={(event) => setFavorite(event.target.value)}
                            options={[{ value: "ALL", label: "Todos" }, { value: "YES", label: "Destacados" }, { value: "NO", label: "No destacados" }]} />
                        <FieldInput id="guides_adv_technology" label="Tecnología" value={useTechnology} onChange={(event) => setTechnology(event.target.value)}
                            placeholder="Ej.: React, NestJS o Docker" />
                        <FieldInput id="guides_adv_version" label="Versión" value={useVersion} onChange={(event) => setVersion(event.target.value)}
                            placeholder="Ej.: 18, 5.6 o 3.12" />
                    </AdvancedFilterPanel>}
                    total={pager.total} totalSingular="grupo" totalPlural="grupos"
                    perPage={pager.usePerPage} onChangePerPage={pager.onChangePerPage}
                    exportColumns={[
                        { key: "name", label: "Grupo" },
                        { key: "category", label: "Categoría" },
                        { key: "technology", label: "Tecnología" },
                        { key: "version", label: "Versión" },
                        { key: "guides", label: "Guías" },
                        { key: "description", label: "Descripción" },
                        { key: "updatedAt", label: "Actualizado" },
                    ]}
                    exportRows={exportRows}
                    exportName="guias"
                    actions={<>
                        <AppButton variant="outline" tone="neutral" icon={<FaChartBar />} isIconOnly title="Estadísticas" aria-label="Estadísticas" onClick={() => setStatisticsOpen(true)} />
                        <AppButton icon={<FaPlus />} onClick={() => { setEditing(null); setIsOpen(true); }}>Nuevo grupo</AppButton>
                    </>}
                />
            </div>

            {!IS_LOADING && filtered.length === 0 && (
                <EmptyState icon={<FaBookOpen />}
                    title={rows.length ? "No encontramos guías" : "Todavía no hay grupos de guías"}
                    description={rows.length ? "Probá con otra búsqueda o limpiá los filtros avanzados." : "Creá un grupo y adentro agregá cada guía o procedimiento que necesites."}
                    action={rows.length
                        ? <AppButton variant="outline" tone="neutral" onClick={() => { setSearch(""); clearAdvanced(); }}>Limpiar filtros</AppButton>
                        : <AppButton icon={<FaPlus />} onClick={() => setIsOpen(true)}>Crear primer grupo</AppButton>} />
            )}

            {IS_LOADING && <div className="LStudio__manual_library_empty">Cargando guías...</div>}

            {!IS_LOADING && pager.paged.length > 0 && (
                <div className="LStudio__manual_library_list LStudio__guide_library_list">
                    {pager.paged.map((group) => (
                        <div key={group.id} className="LStudio__manual_library_row" role="button" tabIndex={0}
                            onClick={() => navigate(`${scopeBase}/${group.id}?mode=read`)}
                            onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") navigate(`${scopeBase}/${group.id}?mode=read`);
                            }}>
                            <div className="LStudio__manual_library_book" style={{ "--manual-color": group.color || "#777" }}>
                                <FaBookOpen />
                            </div>

                            <div className="LStudio__manual_library_main">
                                <div className="LStudio__manual_library_titleline">
                                    <strong>{group.name}</strong>
                                    {group.isFavorite && <FaStar className="LStudio__manual_library_favorite" />}
                                </div>
                                <p>{group.description || "Grupo listo para guardar pasos, procedimientos y guías técnicas."}</p>
                                <div className="LStudio__manual_library_meta">
                                    {group.technology && <span>{group.technology}</span>}
                                    {group.version && <span>v{group.version}</span>}
                                    <span>{CATEGORY_LABEL[group.category] || group.category || "General"}</span>
                                    <span><FaFileAlt /> {group.pageCount || 0} guías</span>
                                    <span>Actualizado {onDate(group.updatedAt)}</span>
                                </div>
                            </div>

                            <div className="LStudio__manual_library_links" onClick={(event) => event.stopPropagation()}>
                                <AppButton size="sm" variant="ghost" tone="neutral" isIconOnly icon={<FaEye />}
                                    aria-label="Ver grupo de guías" onClick={() => navigate(`${scopeBase}/${group.id}?mode=read`)} />
                                <CardActionMenu
                                    title="Descargar grupo"
                                    ariaLabel={`Opciones de descarga de ${group.name || "guías"}`}
                                    items={[
                                        {
                                            key: "download-pdf",
                                            label: useExportingId === group.id ? "Preparando PDF..." : "Descargar PDF completo",
                                            icon: <FaFileAlt />,
                                            isDisabled: !!useExportingId,
                                            onClick: () => onExportGroup(group, "pdf"),
                                        },
                                        {
                                            key: "download-markdown",
                                            label: useExportingId === group.id ? "Preparando Markdown..." : "Descargar Markdown por carpetas (.zip)",
                                            icon: <FaDownload />,
                                            isDisabled: !!useExportingId,
                                            onClick: () => onExportGroup(group, "markdown"),
                                        },
                                    ]}
                                />
                                {group.officialDocsUrl && (
                                    <AppButton size="sm" variant="ghost" tone="neutral" isIconOnly icon={<FaExternalLinkAlt />}
                                        aria-label="Documentación oficial" href={group.officialDocsUrl} />
                                )}
                                <AppButton size="sm" variant="ghost" tone="neutral" isIconOnly
                                    icon={group.isFavorite ? <FaStar /> : <FaRegStar />}
                                    aria-label={group.isFavorite ? "Quitar destacado" : "Destacar"}
                                    onClick={() => onToggleFavorite(group)} />
                                <AppButton size="sm" variant="ghost" tone="neutral" isIconOnly icon={<FaEdit />}
                                    aria-label="Editar grupo de guías" onClick={() => navigate(`${scopeBase}/${group.id}?mode=edit`)} />
                                <AppButton size="sm" variant="ghost" tone="danger" isIconOnly icon={<FaArchive />}
                                    aria-label="Archivar grupo de guías" onClick={() => onArchive(group)} />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <ViewPagination page={pager.usePage} totalPages={pager.totalPages}
                total={pager.total} perPage={pager.usePerPage} onChange={pager.onChangePage} />

            <StatisticsModal isOpen={statisticsOpen} onClose={() => setStatisticsOpen(false)}
                title="Estadísticas · Guías" description={`Resumen de las guías de este ${String(scopeName || "proyecto").toLowerCase()}.`}
                metrics={[
                    { key: "groups", label: "Grupos", value: rows.length, icon: <FaBookOpen />, tone: "neutral", isLoading: IS_LOADING },
                    { key: "guides", label: "Guías", value: totalPages, icon: <FaFileAlt />, tone: "accent", isLoading: IS_LOADING },
                    { key: "favorites", label: "Destacados", value: rows.filter((group) => group.isFavorite).length, icon: <FaStar />, tone: "neutral", isLoading: IS_LOADING },
                    { key: "official", label: "Docs oficiales", value: rows.filter((group) => !!group.officialDocsUrl).length, icon: <FaExternalLinkAlt />, tone: "neutral", isLoading: IS_LOADING },
                ]} />

            <GuideGroupFormModal
                isOpen={useIsOpen}
                onClose={() => { setIsOpen(false); setEditing(null); }}
                group={useEditing}
                onSave={onSave}
                isLoading={useActionApiIsLoading}
                scopeLabel={scopeName}
            />
        </>
    );
};
